import { useState } from 'react';

const FIELD_LIMIT = 12;
const VALUE_LIMIT = 600;
const LINE_LIMIT = 12;

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : typeof value === 'number' ? String(value) : null;
}

function flag(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return null;
  if (['yes', 'true', '1'].includes(value.toLowerCase())) return true;
  if (['no', 'false', '0'].includes(value.toLowerCase())) return false;
  return null;
}

function clipped(value: string): string {
  return Array.from(value).slice(0, VALUE_LIMIT).join('').split('\n').slice(0, LINE_LIMIT).join('\n');
}

function needsClip(value: string): boolean {
  return clipped(value) !== value;
}

function clip(value: string): string {
  return needsClip(value) ? `${clipped(value)}…` : value;
}

function BitLockerSummary({ data }: { data: JsonObject }) {
  const [showAll, setShowAll] = useState(false);
  const windows = isObject(data.Windows) ? data.Windows : {};
  const join = isObject(data.Join) ? data.Join : {};
  const tpm = isObject(data.TPM) ? data.TPM : {};
  const volume = isObject(data.BitLocker) ? data.BitLocker : {};
  const caption = text(windows.Caption);
  const build = text(windows.Build);
  const device = text(data.Device);
  const tenant = text(join.TenantName);
  const entra = flag(join.AzureAdJoined);
  const domain = flag(join.DomainJoined);
  const joined = entra === false && domain === false ? 'Not joined to Entra or a domain.'
    : `${entra === true ? 'Joined to Entra' : entra === false ? 'Not joined to Entra' : 'Entra join unknown'}; ${domain === true ? 'joined to a domain' : domain === false ? 'not joined to a domain' : 'domain join unknown'}.`;
  const present = flag(tpm.TpmPresent);
  const ready = flag(tpm.TpmReady);
  const owned = flag(tpm.TpmOwned);
  const tpmWarnings = [
    flag(tpm.TpmEnabled) === false ? 'disabled' : null,
    flag(tpm.TpmActivated) === false ? 'not activated' : null,
    flag(tpm.LockedOut) === true ? 'locked out' : null,
    flag(tpm.RestartPending) === true ? 'restart pending' : null,
  ].filter((warning): warning is string => warning !== null);
  const protection = typeof volume.ProtectionStatus === 'string' && volume.ProtectionStatus.toLowerCase() === 'off' ? 'off'
    : typeof volume.ProtectionStatus === 'string' && volume.ProtectionStatus.toLowerCase() === 'on' ? 'on' : 'protection unknown';
  const status = typeof volume.VolumeStatus === 'string' ? new Map<string, string>([
    ['fullydecrypted', 'not encrypted'], ['fullyencrypted', 'fully encrypted'], ['encryptioninprogress', 'encryption in progress'],
    ['decryptioninprogress', 'decryption in progress'], ['partiallyencrypted', 'partially encrypted'],
  ]).get(volume.VolumeStatus.toLowerCase()) ?? volume.VolumeStatus : 'encryption state unknown';
  const mount = text(volume.MountPoint);
  const protectors = Array.isArray(volume.KeyProtectors) ? volume.KeyProtectors.length === 0 ? 'no key protectors'
    : `${volume.KeyProtectors.length} key protector${volume.KeyProtectors.length === 1 ? '' : 's'}` : 'key protectors unknown';
  const guidance = text(volume.OperatorGuidance);
  const readErrors = Array.isArray(data.ReadErrors) ? data.ReadErrors.filter((error): error is string => typeof error === 'string' && error.length > 0).join(', ') : '';
  const bounded = [caption, build, device, tenant, mount, status, guidance, readErrors].some(value => value !== null && needsClip(value));
  const show = (value: string | null) => value === null ? null : showAll ? value : clip(value);
  return (
    <>
      <div className="mt-1 space-y-1.5 text-[13px] leading-relaxed text-ink-2 [overflow-wrap:anywhere]">
        <p>{show(caption) ?? 'Windows'}{build ? ` (build ${show(build)})` : ''}. {joined}</p>
        {(device || tenant) && <p>{device && `Device: ${show(device)}`}{device && tenant && ' · '}{tenant && `Entra tenant: ${show(tenant)}`}.</p>}
        <p>TPM: {present === true ? 'present' : present === false ? 'not present' : 'presence unknown'}, {ready === true ? 'ready' : ready === false ? 'not ready' : 'readiness unknown'}, {owned === true ? 'owned' : owned === false ? 'not owned' : 'ownership unknown'}{tpmWarnings.length > 0 ? `, ${tpmWarnings.join(', ')}` : ''}.</p>
        <p>BitLocker on {show(mount) ?? 'volume'} {protection}, {show(status)}, {protectors}.</p>
        {readErrors && <p>Some checks failed: {show(readErrors)}.</p>}
        {guidance && <p>Next step: {show(guidance)}</p>}
      </div>
      {bounded && <button type="button" onClick={() => setShowAll(value => !value)} className="mt-2 text-[12.5px] font-medium text-brand hover:underline">{showAll ? 'Show less' : 'Show all'}</button>}
    </>
  );
}

function displayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return 'Value is too deeply nested to display.';
  }
}

function label(key: string): string {
  const characters = Array.from(key);
  const bounded = characters.length > 120 ? `${characters.slice(0, 120).join('')}…` : key;
  const words = bounded.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/([a-z])([0-9])/gi, '$1 $2');
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

function GenericSummary({ data }: { data: JsonObject }) {
  const [showAll, setShowAll] = useState(false);
  const entries = Object.entries(data).map(([key, value]) => ({ key, value: displayValue(value) }));
  const bounded = entries.length > FIELD_LIMIT || entries.some(entry => needsClip(entry.value));
  const visible = showAll ? entries : entries.slice(0, FIELD_LIMIT);
  return (
    <>
      {entries.length === 0 ? <p className="mt-1 text-[13px] text-ink-3">No fields.</p> : (
        <dl className="mt-1 divide-y divide-line/70">
          {visible.map(entry => (
            <div key={entry.key} className="grid gap-0.5 py-1.5 text-[13px] sm:grid-cols-[minmax(120px,0.35fr)_1fr] sm:gap-3">
              <dt className="font-medium text-ink-3 [overflow-wrap:anywhere]">{label(entry.key)}</dt>
              <dd className="min-w-0 whitespace-pre-wrap text-ink-2 [overflow-wrap:anywhere]">{showAll ? entry.value : clip(entry.value)}</dd>
            </div>
          ))}
        </dl>
      )}
      {bounded && <button type="button" onClick={() => setShowAll(value => !value)} className="mt-2 text-[12.5px] font-medium text-brand hover:underline">{showAll ? 'Show less' : 'Show all'}</button>}
    </>
  );
}

export function RunbookOutputSummary({ stdout, runbookId }: { stdout: string | null; runbookId: string | null }) {
  if (!stdout || !runbookId) return null;
  let data: unknown;
  try {
    data = JSON.parse(stdout);
  } catch {
    return null;
  }
  if (!isObject(data)) return null;
  return (
    <section aria-label="Readable summary" className="mt-3 min-w-0 rounded-xl border border-line bg-panel p-3">
      <h3 className="text-[13px] font-semibold text-ink">Summary</h3>
      {runbookId === 'security.bitlocker-status' ? <BitLockerSummary data={data} /> : <GenericSummary data={data} />}
    </section>
  );
}
